import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/pro-solid-svg-icons";
import axios, { CancelToken } from "axios";

import { GetCustomerMemberships, ICustomerMembership } from "api/rpc/2024-04/customer/membership";
import { StatusCode } from "api/protocols";

import { useGuestPortalContext } from "./GuestPortalContext";

import MembershipDisplay from "elements/customer/membership/MembershipDisplay";
import Spin from "components/spin/spin";
import Page from "components/page/Page";
import Tabs from "components/tabs/Tabs";
import Callout from "components/callout/Callout";
import { ButtonNew as Button } from "components/buttonNew";

import "./portalMembership.scss";
import MembershipDetailsSheet from "components/membershipDetailsSheet/MembershipDetailsSheet";

interface IFilter {
  offset: number;
  limit: number;
}

interface ISelectedMembershipState {
  modalOpen: boolean;
  membership: ICustomerMembership;
}

export default function PortalMembership() {
  const { t } = useTranslation();
  const [membershipState, setMembershipState] = useState<ICustomerMembership[]>(null);
  const [filter, setFilter] = useState<IFilter>({
    offset: 0,
    limit: 25,
  });
  const [selectedMembership, setSelectedMembership] = useState<ISelectedMembershipState>({
    modalOpen: false,
    membership: null,
  });
  const activeMemberships = membershipState?.filter(membership => membership?.status === "active");
  const pastMemberships = membershipState?.filter(membership => membership?.status !== "active");
  const dispatch = useAppDispatch();
  const { portalState } = useGuestPortalContext();

  useEffect(() => {
    const source = axios.CancelToken.source();
    void getMemberships(source.token);

    return () => {
      source.cancel("Cancelled");
    };
  }, [filter]);

  const membershipTabs = [
    {
      id: "active",
      content: "Active",
    },
    {
      id: "inactive",
      content: "Inactive",
    },
  ];

  const [selectedTab, setSelectedTab] = useState<number>(0);

  async function getMemberships(cancelToken: CancelToken) {
    //Display spinner
    if (membershipState !== null) {
      setMembershipState(null);
    }

    const membershipRes = await GetCustomerMemberships({ ...filter }, false, cancelToken);

    if (membershipRes.status !== StatusCode.OK) {
      if (membershipRes?.message !== "Cancelled") {
        dispatch(showError(t("Error getting memeberships")));
      }
      return;
    }

    setMembershipState(membershipRes.data);
  }

  const renderMembershipCards = (memberships: ICustomerMembership[]) => {
    return (
      <>
        {memberships ? (
          <>
            {memberships.length > 0 ? (
              memberships.map(membership => {
                return (
                  <div key={membership.id.toString() + "_active"} style={{ marginBottom: "14px" }}>
                    <MembershipDisplay
                      id={membership.membership_id}
                      logo_image_source={membership.logo_image_source}
                      title={membership.membership_title}
                      type={membership?.membership_type}
                      expiry={membership.expiry}
                      status={membership.status}
                      suspension_start_date={membership.suspension_start_date}
                      suspension_end_date={membership.suspension_end_date}
                      indefinite_suspension={membership.indefinite_suspension}
                      onClick={() => (selectedTab === 0 ? setSelectedMembership({ membership, modalOpen: true }) : {})}
                    />
                  </div>
                );
              })
            ) : (
              <Callout type="info" title={t("No Memberships")} content={t("You do not have any memberships.")} />
            )}
          </>
        ) : (
          <span style={{ display: "flex", justifyContent: "center" }}>
            <Spin />
          </span>
        )}
      </>
    );
  };

  return (
    <Page title={t("Memberships")}>
      <Tabs tabs={membershipTabs} selected={selectedTab} onSelect={setSelectedTab}>
        <div className="portal-memberships-container">
          <div style={{ display: selectedTab === 0 ? "block" : "none", paddingTop: "8px" }}>
            <div>{renderMembershipCards(activeMemberships)}</div>
          </div>

          <div style={{ display: selectedTab === 1 ? "block" : "none", paddingTop: "8px" }}>
            <div>{renderMembershipCards(pastMemberships)}</div>
          </div>
        </div>
        {membershipState && membershipState?.length > 0 && (
          <div className="portal-memberships-footer">
            <Button
              size="small"
              type="secondary"
              onClick={() => setFilter(prev => ({ ...prev, offset: prev.offset - prev.limit }))}
              disabled={!membershipState || !(filter.limit <= filter.offset)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              {t('&nbsp; Prev')}
            </Button>
            <Button
              size="small"
              type="secondary"
              onClick={() => setFilter(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
              disabled={!(membershipState?.length === filter.limit)}
            >
              Next &nbsp;
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        )}
      </Tabs>

      <MembershipDetailsSheet
        open={selectedMembership?.modalOpen}
        enableEmailAction
        cardImageSrc={selectedMembership?.membership?.card_image_source}
        fullName={portalState?.guest?.full_name}
        season={selectedMembership?.membership?.season}
        membershipType={selectedMembership?.membership?.membership_type}
        expiry={selectedMembership?.membership?.expiry}
        membershipTitle={selectedMembership?.membership?.membership_title}
        membershipSubtitle={selectedMembership?.membership?.membership_subtitle}
        memberCode={selectedMembership?.membership?.member_code}
        onCancel={() => setSelectedMembership({ membership: null, modalOpen: false })}
      />
    </Page>
  );
}
